import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ApiError, type components } from '@awj/api-client';
import { Button, Skeleton, useToast } from '@awj/ui';

import { BilingualField } from '~/components/BilingualField.tsx';
import { EmptyState } from '~/components/EmptyState.tsx';
import { FormPage } from '~/components/FormPage.tsx';
import { PageTitle } from '~/components/PageTitle.tsx';
import { Select } from '~/components/Select.tsx';
import { useI18n } from '~/i18n/index.tsx';
import { useCategories, useSaveCategory } from '~/lib/catalog-queries.ts';
import { slugify } from '~/lib/product-form.ts';
import { useUnsavedChanges } from '~/lib/unsaved-changes.tsx';

type Category = components['schemas']['AdminCategoryResponse'];

interface CategoryForm {
  nameAr: string;
  nameEn: string;
  descriptionAr: string;
  descriptionEn: string;
  slugAr: string;
  slugEn: string;
  parentId: string;
  sortOrder: string;
  isActive: boolean;
}

const EMPTY: CategoryForm = {
  nameAr: '',
  nameEn: '',
  descriptionAr: '',
  descriptionEn: '',
  slugAr: '',
  slugEn: '',
  parentId: '',
  sortOrder: '0',
  isActive: true,
};

const from = (category: Category): CategoryForm => ({
  nameAr: category.nameAr,
  nameEn: category.nameEn,
  descriptionAr: category.descriptionAr ?? '',
  descriptionEn: category.descriptionEn ?? '',
  slugAr: category.slugAr,
  slugEn: category.slugEn,
  parentId: category.parentId == null ? '' : String(category.parentId),
  sortOrder: String(category.sortOrder),
  isActive: category.isActive,
});

/**
 * One category, on a page of its own.
 *
 * `new` and an id share the editor, the way the product editor does: the only difference is
 * whether there is anything to load first.
 */
export function CategoryEditorPage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();

  const categories = useCategories();
  const save = useSaveCategory();

  const isNew = id === undefined;
  const all = categories.data ?? [];
  const editing = isNew ? undefined : all.find((category) => String(category.id) === id);

  const [form, setForm] = useState<CategoryForm>(EMPTY);
  const [baseline, setBaseline] = useState<CategoryForm>(EMPTY);
  const unsaved = useUnsavedChanges(JSON.stringify(form) !== JSON.stringify(baseline));
  /*
   * A slug follows the name until somebody types one, and an EXISTING category's slug is already
   * typed — it is in shop links and search results, and renaming a category must not silently
   * move its page.
   */
  const [slugsTouched, setSlugsTouched] = useState(!isNew);
  const [error, setError] = useState<string | null>(null);

  // The list arrives a beat after the page does; fill the form once it has.
  useEffect(() => {
    if (editing) {
      setForm(from(editing));
      setBaseline(from(editing));
      setSlugsTouched(true);
    }
  }, [editing]);

  const back = () => navigate('/categories');

  async function submit(event: React.FormEvent) {
    event.preventDefault();

    if (!form.nameAr.trim() || !form.nameEn.trim() || !form.slugAr.trim() || !form.slugEn.trim()) {
      setError(t('catalog.bothLanguagesRequired'));

      return;
    }

    try {
      await save.mutateAsync({
        id: editing?.id ?? null,
        body: {
          nameAr: form.nameAr.trim(),
          nameEn: form.nameEn.trim(),
          descriptionAr: form.descriptionAr.trim() || null,
          descriptionEn: form.descriptionEn.trim() || null,
          slugAr: form.slugAr.trim(),
          slugEn: form.slugEn.trim(),
          parentId: form.parentId ? Number(form.parentId) : null,
          sortOrder: Math.trunc(Number(form.sortOrder) || 0),
          isActive: form.isActive,
        },
      });

      toast.show({ title: t('catalog.saved'), tone: 'success' });
      unsaved.release();
      back();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? (caught.problem.detail ?? t('catalog.couldNotSave'))
          : t('common.somethingWentWrong'),
      );
    }
  }

  if (!isNew && categories.isPending) {
    return <Skeleton height="520px" />;
  }

  /*
   * An id that matches nothing says so. It used to fall through to the empty form under "Edit
   * category" — and with no category to edit, Save posted a brand-new one.
   */
  if (!isNew && !editing) {
    return (
      <>
        <PageTitle title={t('catalog.editCategory')} back="/categories" />
        <EmptyState
          icon="alert"
          title={categories.isError ? t('common.somethingWentWrong') : t('catalog.categoryNotFound')}
          action={
            categories.isError ? (
              <Button variant="secondary" onClick={() => categories.refetch()}>
                {t('common.retry')}
              </Button>
            ) : (
              <Button variant="secondary" onClick={back}>
                {t('nav.categories')}
              </Button>
            )
          }
          tall
        />
      </>
    );
  }

  return (
    <FormPage
      icon="categories"
      title={isNew ? t('catalog.newCategory') : t('catalog.editCategory')}
      subtitle={t('catalog.categoryDialogLede')}
      onBack={back}
      onSubmit={submit}
      submitLabel={t('common.save')}
      submitting={save.isPending}
    >
      <div className="category-form">
        <BilingualField
          label={t('catalog.name')}
          required
          ar={form.nameAr}
          en={form.nameEn}
          onArChange={(value) =>
            setForm((c) => ({ ...c, nameAr: value, ...(slugsTouched ? {} : { slugAr: slugify(value) }) }))
          }
          onEnChange={(value) =>
            setForm((c) => ({ ...c, nameEn: value, ...(slugsTouched ? {} : { slugEn: slugify(value) }) }))
          }
        />

        <BilingualField
          label={t('catalog.slug')}
          required
          ar={form.slugAr}
          en={form.slugEn}
          onArChange={(value) => {
            setSlugsTouched(true);
            setForm((c) => ({ ...c, slugAr: value }));
          }}
          onEnChange={(value) => {
            setSlugsTouched(true);
            setForm((c) => ({ ...c, slugEn: value }));
          }}
        />

        <BilingualField
          label={t('catalog.description')}
          multiline
          rows={2}
          ar={form.descriptionAr}
          en={form.descriptionEn}
          onArChange={(value) => setForm((c) => ({ ...c, descriptionAr: value }))}
          onEnChange={(value) => setForm((c) => ({ ...c, descriptionEn: value }))}
        />

        {/* Where it sits and whether it is seen — one group, because they are read together. */}
        <div className="category-form__group">
          <span className="category-form__group-title">{t('catalog.placement')}</span>

          <div className="category-form__row">
            <div className="field">
              <label htmlFor="category-parent">{t('catalog.parentCategory')}</label>
              <Select
                id="category-parent"
                value={form.parentId}
                onChange={(parentId) => setForm((c) => ({ ...c, parentId }))}
                placeholder={t('catalog.topLevel')}
                options={all
                  // A category cannot be its own parent, and the shop's tree is two levels deep.
                  .filter((candidate) => candidate.parentId == null && candidate.id !== editing?.id)
                  .map((candidate) => ({
                    value: String(candidate.id),
                    label: locale === 'ar' ? candidate.nameAr : candidate.nameEn,
                  }))}
              />
            </div>

            <label className="field">
              <span>{t('catalog.sortOrder')}</span>
              <input
                className="awj-input numeric"
                type="number"
                step="1"
                value={form.sortOrder}
                onChange={(event) => setForm((c) => ({ ...c, sortOrder: event.target.value }))}
              />
            </label>
          </div>

          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => setForm((c) => ({ ...c, isActive: event.target.checked }))}
            />
            <span>{t('catalog.showInShop')}</span>
          </label>
        </div>

        {error ? (
          <p className="bilingual__error" role="alert">
            {error}
          </p>
        ) : null}
      </div>

      {unsaved.dialog}
    </FormPage>
  );
}
